import { motion } from 'framer-motion';

const programs = [
  {
    icon: 'ri-search-eye-line',
    title: 'Problem Over-Identification Protocol',
    description: 'Our flagship program employs a 47-step methodology to find problems you never knew existed — then find problems within those problems. We don\'t stop until the original issue is buried under layers of newly discovered sub-issues.',
    features: [
      'Initial 47-point problem over-assessment',
      'Sub-problem discovery within every problem found',
      'Recursive issue documentation (problems of problems)',
      'Weekly over-analysis reports',
      'Lifetime problem tracking dashboard'
    ]
  },
  {
    icon: 'ri-arrow-up-circle-line',
    title: 'Strategic Over-Complication Framework',
    description: 'Why have a simple problem when it can be a complex, multi-layered, cross-departmental crisis? Our over-educated team applies a minimum of 14 analytical frameworks to ensure no issue remains straightforward.',
    features: [
      'Multi-tier escalation pathways',
      '14-framework minimum analysis per issue',
      'Complexity amplification protocols',
      'Stakeholder confusion optimization',
      'Crisis escalation velocity maximization'
    ]
  },
  {
    icon: 'ri-brain-line',
    title: 'Premium Over-Thinking Immersion',
    description: 'True transformation begins with relentless over-thinking. Our immersive program ensures every situation is analyzed from every conceivable angle — and several inconceivable ones — until the original problem is barely recognizable.',
    features: [
      '24/7 over-thinking monitoring service',
      'Daily recursive analysis sessions',
      'Inconceivable-angle exploration workshops',
      'Mindful issue over-contemplation training',
      'Perpetual vigilance sustainability coaching'
    ]
  },
  {
    icon: 'ri-add-circle-line',
    title: 'Complimentary Bonus Problem Suite',
    description: 'As a value-added service, our process organically generates new problems at no extra charge. Think of it as compound interest, but for anxiety. Every engagement is guaranteed to produce a minimum of three bonus problems.',
    features: [
      'Minimum 3 bonus problems per engagement — guaranteed',
      'Bonus problem categorization and documentation',
      'Blockchain-verified bonus problem logging',
      'Historical bonus problem preservation',
      'Multi-generational bonus problem inheritance planning'
    ]
  }
];

const InvestmentStrategies = () => {
  return (
    <section id="programs" className="relative py-32 bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-center justify-center gap-3 mb-6"
          >
            <div className="w-12 h-[2px] bg-gold"></div>
            <span className="text-gold text-sm font-semibold tracking-widest uppercase">
              Our Core Programs
            </span>
            <div className="w-12 h-[2px] bg-gold"></div>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl font-black text-white mb-6"
          >
            We Don't Just Find Problems. <span className="text-gold">We Multiply Them.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-cream/70 max-w-3xl mx-auto"
          >
            Each program is meticulously over-engineered by our team of dangerously over-educated professionals 
            to ensure your problems are thoroughly complicated, relentlessly over-thought, and guaranteed never to run out.
          </motion.p>
        </div>

        {/* Programs Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {programs.map((program, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative bg-gradient-to-br from-zinc-900 to-black border border-gold/20 rounded-2xl p-8 hover:border-gold/50 transition-all duration-500"
            >
              {/* Icon */}
              <div className="w-16 h-16 flex items-center justify-center bg-gold/10 rounded-xl mb-6 group-hover:bg-gold/20 transition-colors duration-300">
                <i className={`${program.icon} text-3xl text-gold`}></i>
              </div>

              {/* Content */}
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors duration-300">
                {program.title}
              </h3>
              <p className="text-cream/70 mb-6 leading-relaxed">
                {program.description}
              </p>

              {/* Features */}
              <ul className="space-y-3">
                {program.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-cream/60">
                    <i className="ri-checkbox-circle-fill text-gold mt-1 flex-shrink-0"></i>
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Decorative Element */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold/5 rounded-full blur-3xl group-hover:bg-gold/10 transition-all duration-500"></div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <p className="text-cream/60 mb-6 text-lg">
            All programs include lifetime access to our Bonus Problem Archive and a certificate of Over-Complication Excellence
          </p>
          <a
            href="#pricing"
            className="inline-flex items-center gap-3 bg-gold text-black px-8 py-4 rounded-full text-base font-bold hover:bg-white transition-all duration-300 whitespace-nowrap"
          >
            View Investment Options
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default InvestmentStrategies;